import React, { useEffect, useState } from "react";
import CustomDataGrid from "../../components/Common/CustomDataGrid";
import { getInspectionGeneratedReports, previewInspectionDocument, downloadInspectionDocument, getInspectionReviewWorkflowTasks, getLoggedInUser, completeInspectionReviewWorkflowTask } from "../../helpers/backend_helper";
import { ErrorNotify, SuccessNotify } from "../../shared/Alert";

export default function ReviewReports({ review }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);
  const recoverReports = async () => {
    setBusy("recover");
    try {
      const [tasks, user] = await Promise.all([getInspectionReviewWorkflowTasks(review.id), getLoggedInUser()]);
      if (!tasks?.status) throw new Error(tasks?.message || "Unable to load review assignments.");
      const task = (tasks.data || []).find(item => item.workflowStatus === "Completed" &&
        Number(item.assignedTo) === Number(user?.id || user?.userId) &&
        ["ChecklistReview", "ChecklistSignOff"].includes(item.taskType));
      if (!task) throw new Error("No submitted checklist assignment is available for your user.");
      const result = await completeInspectionReviewWorkflowTask(review.id, task.id);
      if (!result?.status) throw new Error(result?.message || "Unable to recover reports.");
      const refreshed = await getInspectionGeneratedReports(review.id);
      if (!refreshed?.status) throw new Error("Unable to refresh reports.");
      setReports(refreshed.data || []);
      const message = result.data?.reportMessage || (result.data?.generatedReports ? "Missing reports generated." : "No additional reports were generated.");
      if (message.includes("waiting for")) ErrorNotify(message);
      else SuccessNotify(message);
    } catch (e) { ErrorNotify(e.message); }
    finally { setBusy(null); }
  };
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    getInspectionGeneratedReports(review.id).then((r) => {
      if (!r?.status) throw new Error(r?.message || "Unable to load reports.");
      if (active) setReports(r.data || []);
    }).catch((e) => { if (active) setError(e.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [review.id]);
  const open = async (report, download) => {
    setBusy(report.id);
    try {
      if (download) await downloadInspectionDocument(review.id, report.id, report.fileName || report.name || "report.pdf");
      else await previewInspectionDocument(review.id, report.id);
    } catch (e) { ErrorNotify(e.message || "Unable to open report."); }
    finally { setBusy(null); }
  };
  return <section className="review-workspace__register">
    <h2>Reports</h2><p>All generated reports for this review of <strong>{review.engagementDetail?.name}</strong>, across all checklists.</p>
    <button type="button" className="checklist-review__record-open" style={{ marginBottom: 12 }} disabled={busy !== null || loading} onClick={recoverReports}>{busy === "recover" ? "Generating…" : "Generate Missing Reports"}</button>
    {loading ? <p role="status">Loading reports…</p> : error ? <p role="alert">{error}</p> :
      <CustomDataGrid data={reports} showToolbar={false} recordLabel="reports" initialPageSize={25}
        gridSx={{ '& .MuiDataGrid-cell[data-field="actions"]': { display: "flex", alignItems: "center", gap: "8px" } }}
        columns={[
          { id: "name", header: "Report", accessorFn: (r) => r.fileName || r.name || "Report", flex: 2 },
          { id: "description", header: "Description", accessorFn: (r) => r.description || "—", flex: 2 },
          { id: "date", header: "Generated On", accessorFn: (r) => r.createdOn ? new Date(r.createdOn).toLocaleString() : "—" },
          { id: "actions", header: "Action", accessorFn: () => "", size: 210, flex: 0, enableSorting: false, enableColumnFilter: false,
            cell: ({ row }) => <>{[false, true].map((download) => <button key={String(download)} type="button" className="checklist-review__record-open" disabled={busy === row.original.id} onClick={() => open(row.original, download)}>{download ? "Download" : "View"}</button>)}</> },
        ]} emptyState={{ title: "No reports generated for this review", description: "Generated review reports will appear here." }} />}
  </section>;
}
